let padInt = function(int, padSize, padChar) {
	padSize = padSize || 2;
	padChar = '0';
	return (padChar.repeat(padSize)+int.toString()).substr(-padSize);
}

// How many gift/sticker images a message (or a combo merged into it) stacks up before
// folding the rest into a "+N" badge instead — shared by the chat widget and the admin
// chat/alert log, so both cap and badge the same way. See createChatLogMessage and
// placeChatImages.
const CHAT_IMAGE_STACK_LIMIT = 10;

export default {
	escapeHtml: function(unsafe) {
		return (unsafe||'')
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	},

	buildQueryString: function(params) {
		return new URLSearchParams(params).toString();
	},

	decodeEntities: function(encodedString) {
		var translate_re = /&(nbsp|amp|quot|lt|gt);/g;
		var translate = {
			"nbsp":" ",
			"amp" : "&",
			"quot": "\"",
			"lt"  : "<",
			"gt"  : ">"
		};
		return encodedString.replace(translate_re, function(match, entity) {
			return translate[entity];
		}).replace(/&#(\d+);/gi, function(match, numStr) {
			var num = parseInt(numStr, 10);
			return String.fromCharCode(num);
		});
	},

	padInt: padInt,

	secondsToTime: function(sec) {
		if(typeof sec === 'undefined' || sec === null)
			return '--:--';

		sec = sec || 0;
		sec = Math.floor(sec);

		var s = sec % 60;
		var m = Math.floor(sec / 60) % 60;
		var h = Math.floor(sec / 60 / 60);

		var str =
			'' +
			(h ? `${h}:` : '') +
			(h ? padInt(m, 2, '0') : (m ? `${m}` : '0')) +
			':' + (padInt(s||0, 2, '0'));

		return str;
	},

	generateRandomString(length) {
		var result = '';

		var randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		for(var i = 0; i < length; i++) {
			result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
		}

		return result;
	},

	trim(str) {
		return str.replace(/^[ \t\r\n]+/, '').replace(/[ \t\r\n]+$/, '');
	},

	normalizeSpaces(str) {
		return str.replace(/[ \t\r\n]+/g, ' ');
	},

	// https://stackoverflow.com/questions/36734201/how-to-convert-numbers-to-million-in-javascript
	moneyString(value, digits) {
		if(value === null)
			return null;

		if(!value) return '-';

		digits = digits || 1;

		var n = Math.abs(Number(value));
		var ret =
			n >= 1.0e+9
			?
				(n / 1.0e+9).toFixed(digits) + "B"
			:
				n >= 1.0e+6 // Six Zeroes for Millions
				?
					(n / 1.0e+6).toFixed(digits) + "M"
				:
					n >= 1.0e+3 // Three Zeroes for Thousands
					?
						(n / 1.0e+3).toFixed(digits) + "K"
					:
						n;

		return ret;
	},

	bytesString(n, precision) {
		if(n === 0) return '0';
		const units = ['','K','M','G','T','P','E'];
		const i = Math.floor(Math.log(n) / Math.log(1024));
		const value = n / Math.pow(1024, i);
		if(typeof precision === 'undefined') precision = 1;
		return `${parseFloat(value.toFixed(precision))}${units[i]}`;
	},

	dateToSqlString(date) {
		return (typeof date === 'undefined') ? date : `${date.getFullYear()}-${padInt(date.getMonth() + 1, 2, '0')}-${padInt(date.getDate(), 2, '0')} ${padInt(date.getHour(), 2, '0')} ${padInt(date.getMinute(), 2, '0')} ${padInt(date.getSecond(), 2, '0')}`;
	},

	sqlStringToDate(str) {
		if(!str)
			return str;

		var t = str.split(/[- :T.]/);
		return new Date(Date.UTC(parseInt(t[0]), parseInt(t[1])-1, parseInt(t[2]), parseInt(t[3]), parseInt(t[4]), parseInt(t[5])));
	},

	randomItem(items) {
		if(Array.isArray(items)) {
			items = items.filter(a => a !== null && typeof a !== 'undefined');
			return items[Math.floor(Math.random() * items.length)];
		}

		return items;
	},
	setTimeoutPromise(ms, signal) {
		return new Promise((resolve, reject) => {
			const tid = setTimeout(resolve, ms);
			signal?.addEventListener('abort', event => {
				clearTimeout(tid);
				reject(event);
			});
		});
	},
	setImmediatePromise() {
		return new Promise(resolve => setImmediate(resolve));
	},
	fetchWithTimeout(url, options, timeoutMs) {
		const controller = new AbortController();
		const id = timeoutMs && setTimeout(() => controller.abort(), timeoutMs);
		return fetch(url, { ...options, signal: controller.signal }).finally(() => {
			if(id) clearTimeout(id);
		});
	},

	// Return a promise that resolves when all media elements inside a given element have been loaded
	awaitMediaLoadedRecursive(elem, timeout) {
		timeout = timeout || 1000;
		return Promise.race([
			this.setTimeoutPromise(timeout),
			Promise.all(Array.from(elem.querySelectorAll('img,video,audio')||[]).map(elem => new Promise(resolve => {
				if(elem.complete) return resolve();
				const loaded = () => {
					elem.removeEventListener('load', loaded);
					resolve();
				};
				elem.addEventListener('load', loaded);
				const errored = () => {
					elem.removeEventListener('error', errored);
					resolve(); // we don't really care if there's an error
				};
				elem.addEventListener('error', errored);
			}))),
		]);
	},

	relativeDate(date) {
		const diff = Math.round((new Date() - new Date(date)) / 1000);

		const minute = 60;
		const hour = minute * 60;
		const day = hour * 24;
		const week = day * 7;
		const month = day * 30;
		const year = month * 12;

		if (diff < 30) {
			return "just now";
		} else if (diff < minute) {
			return diff + " seconds ago";
		} else if (diff < 2 * minute) {
			return "a minute ago";
		} else if (diff < hour) {
			return Math.floor(diff / minute) + " minutes ago";
		} else if (Math.floor(diff / hour) == 1) {
			return "1 hour ago";
		} else if (diff < day) {
			return Math.floor(diff / hour) + " hours ago";
		} else if (diff < day * 2) {
			return "yesterday";
		} else if (diff < week) {
			return week + " days ago";
		} else if (diff < month) {
			return Math.floor(diff / week) + " weeks ago";
		} else if (diff < year) {
			return Math.floor(diff / month) + " months ago";
		} else {
			const years = Math.floor(diff / year);
			return `${years} year${years == 1 ? '' : 's'} ago`;
		}
	},

	getLocationQueryParam(paramName) {
		const params = new Proxy(new URLSearchParams(window.location.search), {
			get: (searchParams, prop) => searchParams.get(prop),
		});

		return params[paramName];
	},

	escapeRegExp(str) {
		return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	},

	generateSpreadShadowFilter(spread, blur, color) {
		spread = spread || 0;
		blur = blur || 0;
		if(!spread && !blur) return 'none';
		const ret = [];
		const nPoints = 4;
		const delta = (Math.PI * 2) / nPoints;
		for(let n = 0, a = 0; n < nPoints; n++, a += delta) {
			const x = Math.round(spread * Math.cos(a));
			const y = Math.round(spread * Math.sin(a));
			ret.push(`drop-shadow(${x}px ${y}px ${blur}px ${color})`);
		}
		return ret.join(' ');
	},

	getIndefiniteArticle(word) {
		const vowels = ['a', 'e', 'i', 'o', 'u'];
		const lowercaseWord = word.toLowerCase();

		// Known initialisms (spoken as individual letters)
		const initialisms = ['FBI', 'CIA', 'NSA', 'MRE', 'MRI', 'UN', 'EU', 'UFO'];

		if (initialisms.includes(word)) {
			const pronouncedWithAn = ['A', 'E', 'F', 'H', 'I', 'L', 'M', 'N', 'O', 'R', 'S', 'X'];
			return pronouncedWithAn.includes(word[0]) ? 'an' : 'a';
		}

		// Special cases for regular words
		if (/^hour/.test(lowercaseWord)) return 'an';
		if (/^honest/.test(lowercaseWord)) return 'an';
		if (/^heir/.test(lowercaseWord)) return 'an';
		if (/^u[bcfhjkqrstnlg]/.test(lowercaseWord)) return 'a'; // university, user

		return vowels.includes(lowercaseWord[0]) ? 'an' : 'a';
	},

	generateValueString(value, currency, options) {
		const digits = value > 0 && value < 0.01 ? 6 : 2;
		try {
			return `${new Intl.NumberFormat(navigator.language||'en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: digits, ...(options||{}) }).format(value)}`;
		} catch(e) {
			return `${Number(Number(value).toFixed(digits))} ${currency}`;
		}
	},

	generateGiftString(message, giftType) {
		const isFancy = giftType?.value && giftType?.currency;
		const multiplier = message.multiplier||1;
		const string = isFancy ? giftType?.string||`sent ${this.getIndefiniteArticle(message.giftName||giftType?.name||message.giftType)} {type}` : '';
		const stringMulti = isFancy ? giftType?.string_multi||'sent {multiplier}×{type}' : '';
		let displayString = `${(multiplier == 1 ? string : stringMulti).replace('{type}', message.giftName||giftType?.name||message.giftType).replace('{multiplier}', multiplier).replace('#', multiplier)}`;
		let ttsString = isFancy ? displayString.replaceAll('_', ' ') : '';

		if(message.currency) {
			let valueString = this.generateValueString(message.value * multiplier, message.currency);
			if(!isFancy) ttsString += ` sent ${valueString}`;

			if(message.convertedValue && message.convertedCurrency && message.convertedCurrency.toLowerCase() != message.currency.toLowerCase()) {
				valueString += ' / ';
				valueString += this.generateValueString(message.convertedValue * multiplier, message.convertedCurrency);
			}
			displayString += displayString ? ` (${valueString})` : valueString;
		}

		if(message.product) {
			const productString = `bought a ${message.product}`;
			if(displayString) displayString += ' and ';
			if(ttsString) ttsString += ' and ';
			displayString += productString;
			ttsString += productString;
		}

		ttsString += ` on ${message.serviceName}`;

		return { displayString, isFancy, ttsString };
	},

	// proxy: true routes an image through our own CORS proxy, for a host that serves no
	// Access-Control-Allow-Origin header. attrs: [{name, value}] carries any other
	// attribute the source needs (e.g. crossorigin) — see applyAttrs.
	getImageSrc(span) {
		if(!span.proxy) return span.src;
		const baseUrl = process.env.VUE_APP_PIPIPUPUBASE || `${location.protocol}//${location.host}`;
		return `${baseUrl}/proxy/${encodeURIComponent(span.src)}`;
	},

	getAttrValue(attrs, name) {
		return attrs?.find(a => a.name === name)?.value;
	},

	applyAttrs(node, attrs) {
		for(const { name, value } of attrs || []) node.setAttribute(name, value);
	},

	spansToHtml(spans) {
		return (spans||[]).filter(s => s).map(span => {
			if(typeof span == 'string') return this.escapeHtml(span);
			if(span.type == 'br') return '<br />';
			if(span.text) {
				let html = this.escapeHtml(span.text);
				const tagTypes = {
					bold: 'B',
					italic: 'I',
					underline: 'U',
					strikethrough: 'S',
					code: 'CODE',
				};
				for(let attr in tagTypes) {
					if(!span[attr]) continue;
					html = `<${tagTypes[attr]}>${html}</${tagTypes[attr]}>`;
				}
				if(span.color || span.bgColor)
					html = `<span style="${span.color ? `color:${this.escapeHtml(span.color)};` : ''}${span.bgColor ? `background-color:${this.escapeHtml(span.bgColor)};` : ''}">${html}</span>`;
				return html;
			}
			if(span?.type == 'image' || span?.type == 'emote' || span?.type == 'sticker') {
				let html = `
					<${span.video ? 'video' : 'img'}
						class="${span.type}${span.class ? ` ${span.class}` : ''}"
						alt="${this.escapeHtml(span.alt||'')}"
						referrerpolicy="no-referrer"
						${(span.attrs||[]).map(a => `${this.escapeHtml(a.name)}="${this.escapeHtml(a.value)}"`).join(' ')}
						${span.title ? ` title="${this.escapeHtml(span.title)}"` : ''}
						${span.video ? `><source src="${this.escapeHtml(this.getImageSrc(span))}" /></video` : `src="${this.escapeHtml(this.getImageSrc(span))}"`}
					>
				`;
				return html;
			}
			return null;
		}).filter(s => s).join('');
	},

	// Matches http(s):// links as well as bare www. links, stopping before
	// trailing punctuation that is typically not part of the URL.
	urlRegex: /(?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"]/gi,

	// Turns a plain text string into an array of DOM nodes, replacing any URLs
	// with anchor elements. Returns text nodes and <a> elements.
	linkify(text) {
		const nodes = [];
		let lastIndex = 0;
		let match;
		const regex = new RegExp(this.urlRegex.source, 'gi');
		while((match = regex.exec(text))) {
			if(match.index > lastIndex)
				nodes.push(document.createTextNode(text.slice(lastIndex, match.index)));
			const raw = match[0];
			const anchor = document.createElement('a');
			anchor.href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
			anchor.target = '_blank';
			anchor.rel = 'noopener noreferrer nofollow';
			anchor.textContent = raw;
			// Don't let clicking a link also trigger the message's click handler.
			anchor.onclick = e => e.stopPropagation();
			nodes.push(anchor);
			lastIndex = regex.lastIndex;
		}
		if(lastIndex < text.length)
			nodes.push(document.createTextNode(text.slice(lastIndex)));
		return nodes;
	},

	spansToDom(spans, options = {}) {
		return (spans||[]).filter(s => s).flatMap(span => {
			if(typeof span == 'string')
				return options.linkify ? this.linkify(span) : [ document.createTextNode(span) ];
			if(span.type == 'br') return [ document.createElement('br') ];
			if(span.text) {
				const children = options.linkify ? this.linkify(span.text) : [ document.createTextNode(span.text) ];
				const tagTypes = {
					bold: 'B',
					italic: 'I',
					underline: 'U',
					strikethrough: 'S',
					code: 'CODE',
				};
				// Build the (possibly nested) formatting wrapper, then place the
				// text/link nodes inside the innermost element.
				let outer = null;
				let inner = null;
				for(let attr in tagTypes) {
					if(!span[attr]) continue;
					const e = document.createElement(tagTypes[attr]);
					if(inner) inner.appendChild(e); else outer = e;
					inner = e;
				}
				if(!outer && (span.color || span.bgColor)) {
					// No formatting tag but we still need an element to carry the style.
					outer = inner = document.createElement('span');
				}
				if(inner) {
					children.forEach(n => inner.appendChild(n));
					if(span.color) outer.style.color = span.color;
					if(span.bgColor) outer.style.backgroundColor = span.bgColor;
					return [ outer ];
				}
				return children;
			}
			if(span?.type == 'image' || span?.type == 'emote' || span?.type == 'sticker') {
				const node = document.createElement(span.video ? 'video' : 'img');
				node.classList.add(span.type);
				node.referrerPolicy = 'no-referrer';
				this.applyAttrs(node, span.attrs);
				node.src = this.getImageSrc(span);
				if(span.title) node.title = span.title;
				node.alt = span.alt || '';
				if(span.class) node.classList.add(span.class);
				return [ node ];
			}
			return [];
		}).filter(s => !!s);
	},

	// The event type a message is configured and filtered under, which is not always msg.type:
	// anything carrying a gift value counts as a gift (a chat message can), and a message sent
	// from the dashboard is a chat message.
	resolveEventType(msg) {
		if(!msg) return null;
		if(msg.giftType && msg.value > 0) return 'gift';
		if(msg.type == 'dashboardMessage') return 'chat';
		return msg.type;
	},

	prepareMessage(msg, fields, data) {
		const ret = {
			message: msg,
			serviceId: msg?.serviceId,
			token: msg?.id,
			dbId: msg.dbId,
			id: `${msg.serviceId}-${msg.id||this.generateRandomString(20)}`,
			channelToken: msg.target?.channelToken,
			chatToken: msg.target?.chatToken,
			chatterToken: msg.sender?.token||msg.sender?.id||msg.sender?.username||msg.sender?.displayName||'-',
		};

		if(fields.serviceName) ret.serviceName = msg.serviceName;
		if(fields.serviceIcon || fields.serviceIconUrl) {
			if(msg.serviceIconUrl || (String(msg?.serviceId) in data.serviceIcons)) {
				ret.serviceIconUrl = msg.serviceIconUrl||data.serviceIcons[msg.serviceId];
				if(fields.serviceIcon && ret.serviceIconUrl) {
					ret.serviceIcon = document.createElement('IMG');
					ret.serviceIcon.src = ret.serviceIconUrl;
					ret.serviceIcon.alt = msg.serviceName;
					ret.serviceIcon.title = msg.serviceName;
					ret.serviceIcon.classList.add('service');
				}
			}
		}

		if(fields.avatarIcon || fields.avatarUrl) {
			ret.avatarUrl = msg.sender?.avatar;
			if(fields.avatarIcon && ret.avatarUrl) {
				const avatarElem = document.createElement('IMG');
				avatarElem.src = msg.sender?.avatar;
				avatarElem.classList.add('avatar');
				ret.avatarIcon = avatarElem;
			}
		}

		ret.displayName = msg.sender?.displayName || msg.sender?.username || msg.sender?.token;
		if(fields.displayNameElem && ret.displayName) {
			const nameElem = document.createElement('B');
			nameElem.classList.add('name');
			nameElem.innerText = ret.displayName;
			ret.displayNameElem = nameElem;
		}

		const isGift = msg.giftType && msg.value > 0;
		if(fields.isGift) ret.isGift = isGift;

		const isSubscription = msg.type=='subscription' || msg.type=='subGift' || msg.type=='subExtend' || msg.type=='subStreak';
		if(fields.isSubscription) ret.isSubscription = isSubscription;
		const isProduct = !!msg.product;
		const otherAlerts = [ 'follow', 'host', 'subGiftReceive', 'subStreak', 'like', 'share', 'tower', 'goal', 'subGoal', 'followerGoal', 'alert' ];
		const isAlert = isGift || isSubscription || isProduct || otherAlerts.includes(msg.type);
		if(fields.isAlert) ret.isAlert = isAlert;

		const isHighlighted = msg.serviceId && data?.user?.highlighted_message?.serviceId == msg.serviceId && data?.user?.highlighted_message?.token == msg.id;
		if(fields.isHighlighted) ret.isHighlighted = isHighlighted;

		if((fields.giftAmount || fields.speakProlog || fields.sounds) && isGift) ret.giftAmount = this.generateValueString(msg.value * (msg.multiplier||1), msg.currency);

		if(isGift) {
			const { displayString, ttsString } = this.generateGiftString(msg, data?.giftTypes?.[msg.serviceId]?.[msg.giftType]);
			ret.giftString = displayString;
			ret.speakProlog = !msg.ttsUrl && !msg.externalTtsUrl && `${ret.displayName} ${ttsString}`;
		}

		if(fields.giftElem && ret.giftString) {
			ret.giftElem = document.createElement('span');
			if(isGift)
				ret.giftElem.classList.add('gift');
			if(msg.product)
				ret.giftElem.classList.add('merch');

			ret.giftElem.appendChild(document.createTextNode(ret.giftString));
		}

		if(fields.song) ret.song = msg.song;
		if(msg.type != 'tower') {
			if(fields.media) ret.media = msg.media;
			if(fields.mediaString || fields.mediaElem) {
				if(msg.media) {
					ret.mediaString = `${msg.mediaTitle||msg.media}${msg.mediaDuration?` (${this.secondsToTime(msg.mediaDuration)})`:''}`;
					const mediaUrl = (typeof msg.media == 'string' && /^https?:\/\//.test(msg.media)) ? msg.media : null;
					if(mediaUrl) {
						ret.mediaElem = document.createElement('a');
						ret.mediaElem.href = mediaUrl;
						ret.mediaElem.target = '_blank';
						ret.mediaElem.rel = 'noopener noreferrer';
						// Don't let the link click bubble up to the message-select handler.
						ret.mediaElem.onclick = ev => ev.stopPropagation();
					} else {
						ret.mediaElem = document.createElement('div');
					}
					ret.mediaElem.classList.add('media');
					ret.mediaElem.appendChild(document.createTextNode(ret.mediaString));
				}
			}
		}
		const thresholdValue = msg.convertedValue || 1;
		if(fields.giftColorThreshold && isGift)
			ret.giftColorThreshold = this.getGiftThreshold(thresholdValue, data.giftColorThresholds||[]);

		if(fields.eventString) {
			// A bot may supply a ready-made eventString (e.g. YouTube's own membership
			// announcement text); use it verbatim instead of generating one.
			if(msg.eventString) ret.eventString = msg.eventString;
			else switch(msg.type) {
				case 'timeout': ret.eventString = `was timed out${msg.seconds > 0 ? ` for ${this.timeDuration(msg.seconds)}` : ''}`; break;
				case 'subscription': ret.eventString = msg.isUpgrade
					? `upgraded${msg.tier ? ` to ${msg.tier}` : ' their membership'}`
					: `joined${msg.tier ? ` ${msg.tier}` : ''}${msg.numMonths ? ` for ${this.monthDuration(msg.numMonths)}` : ''}`; break;
				case 'subGift': ret.eventString = `gifted ${this.formatAmountLabel(msg.count||1, 'membership')}${msg.receiver?` to ${msg.receiver}`:''}`; break;
				case 'subGiftReceive': ret.eventString = `received a membership${msg.gifter ? ` from ${msg.gifter}` : ''}`; break;
				case 'subStreak': ret.eventString = `has been a member for ${this.monthDuration(msg.length)}${msg.tier ? ` (${msg.tier})` : ''}`; break;
				case 'ban': ret.eventString = `was banned${msg.bannedBy?` by ${msg.bannedBy.displayName||msg.bannedBy.displayName}`:''}${msg.reason?`: ${msg.reason}`:''}`; break;
				case 'unban': ret.eventString = `was unbanned${msg.unbannedBy?` by ${msg.unbannedBy.displayName}`:''}${msg.reason?`: ${msg.reason}`:''}`; break;
				case 'moderator': ret.eventString = `${msg.add?`was made moderator`:`is no longer a moderator`}`; break;
				case 'host': ret.eventString = `hosted${msg.numViewers?` with ${msg.numViewers} viewers`:''}`; break;
				case 'live': ret.eventString = `is streaming ${msg.title||''} ${msg.url?` at ${msg.url}`:''}`; break;
				case 'offline': ret.eventString = `is offline ${msg.url?` at ${msg.url}`:''}`; break;
				case 'delete': ret.eventString = `deleted ${msg.ids?` ${msg.ids.join(', ')}`:''}`; break;
				case 'follow': ret.eventString = `followed`; break;
				case 'unfollow': ret.eventString = `unfollowed`; break;
				case 'join': ret.eventString = `joined`; break;
				case 'leave': ret.eventString = `left`; break;
				case 'like': ret.eventString = `liked the stream`; break;
				case 'share': ret.eventString = `shared the stream`; break;
				case 'subExtend': ret.eventString = `extended their membership${msg.length?` for ${this.monthDuration(msg.length)}`:''}`; break;
				case 'tower': ret.eventString = msg.message || `Tower built: ${msg.string}`; break;
				case 'goal': ret.eventString = `completed the ${msg.goal ? this.generateValueString(msg.goal, msg.currency) : ''} gift goal`; break;
				case 'subGoal': ret.eventString = `completed the ${msg.goal ? this.formatAmountLabel(msg.goal||1, 'membership', { article: false }) : 'membership'} goal`; break;
				case 'followerGoal': ret.eventString = `completed the ${msg.goal ? this.formatAmountLabel(msg.goal||1, 'follower', { article: false }) : 'follower'} goal`; break;
			}
		}

		if(fields.shortEvent) {
			switch(msg.type) {
				case 'subscription': ret.shortEvent = msg.isUpgrade ? 'Upgrade' : 'Join'; break;
				case 'subGift': ret.shortEvent = `Gift ${this.formatAmountLabel(msg.count||1, 'membership')}`; break;
				case 'subGiftReceive': ret.shortEvent = `Get membership`; break;
				case 'subStreak': ret.shortEvent = `${msg.length}mo member${msg.tier ? ` (${msg.tier})` : ''}`; break;
				case 'host': ret.shortEvent = `Host ${msg.numViewers?` (${msg.numViewers})`:''}`; break;
				case 'follow': ret.shortEvent = `Follow`; break;
				case 'subExtend': ret.shortEvent = `Extend membership ${msg.length?` ${this.monthDuration(msg.length)}`:''}`; break;
				case 'chat': if(isProduct) ret.shortEvent = 'Merch'; break;
				case 'tower': ret.shortEvent = msg.string||'Tower'; break;
				case 'goal': ret.shortEvent = 'Goal'; break;
				case 'subGoal': ret.shortEvent = 'Membership goal'; break;
				case 'followerGoal': ret.shortEvent = 'Follower goal'; break;
			}
		}

		if(fields.spansDom || fields.spansHtml || fields.speak || fields.sounds) {
			const doReplacements = (str, internal) => (data.textReplacements||[]).filter(r => !r.internal||internal).reduce((accum, val) => accum.replaceAll(val.expression, val.replacement), str||'');
			const doSpansReplacements = (spans, internal) => (spans||[]).map(s => {
				if(typeof s == 'string') return doReplacements(s, internal);
				if('text' in s) s = { ...s, text: doReplacements(s.text, internal) };
				if('title' in s) s = { ...s, title: doReplacements(s.title, internal) };
				if('alt' in s) s = { ...s, alt: doReplacements(s.alt, internal) };
				return s;
			});
			if(fields.spansDom)
				ret.spansDom = this.spansToDom(doSpansReplacements(msg.spans), { linkify: fields.linkifySpans });
			if(fields.spansHtml)
				ret.spansHtml = msg.message?.string || this.spansToHtml(doSpansReplacements(msg.spans));
			if((fields.speak || fields.sounds) && !data.ignoreSpeak && !msg.sender?.isSelf && !msg.mml && msg.sender?.settings?.tts_status !== 'muted')
				ret.speak = (doSpansReplacements(msg.spans, true)).map(s => this.trim(this.normalizeSpaces(typeof s === 'string' ? s : (s.text || s.title || '')))).filter(a => !!a).join(' ');
		}

		if(fields.classList) {
			ret.classList = [];
			ret.classList.push('message');
			ret.classList.push(msg.type);
			if(msg.sender?.isSelf) ret.classList.push('self');
			if(msg.sender?.isOwner) ret.classList.push('owner');
			if(msg.sender?.isModerator) ret.classList.push('moderator');
			if(msg.sender?.isAnonymous) ret.classList.push('anonymous');
			if(isHighlighted) ret.classList.push('highlighted');
			if(msg.loading) ret.classList.push('loading');
			if(isSubscription) ret.classList.push('subscription');
			if(isGift) ret.classList.push('gift');
			if(isProduct) ret.classList.push('product');
			if(ret.giftColorThreshold) ret.classList.push(`gift-${ret.giftColorThreshold.value}`);
		}
		if(fields.canReply) ret.canReply = !!data?.channels?.find(c => c.service_id == msg?.serviceId && c.token == msg?.target?.channelToken);

		if(fields.group && isGift) ret.group = msg.giftType && data.giftTypes?.[msg.serviceId]?.[msg.giftType]?.group || false;
		// giftName too: YouTube falls back to a shared generic giftType ('gift') for
		// jewel gifts it can't slug from the icon URL, which would otherwise merge
		// different named gifts from the same chatter into one combo.
		if(fields.groupId && isGift) ret.groupId = [ msg.serviceId, ret.chatterToken, msg.giftType, msg.giftName ].map(a => a||'').join('-');

		if(fields.images) {
			if(isGift) {
				const giftMedia = data.giftTypes?.[msg.serviceId]?.[msg.giftType]?.media || [];
				const thresholdAny = data.giftMediaThresholds?.toReversed?.()?.find?.(t => t.amount - .001 <= thresholdValue && t.files?.length > 0);
				const thresholdFiles = thresholdAny?.files || [];
				// Custom media set for this gift type (a streamer's own, or the app-wide
				// fallback image) outranks the bot's own icon — that's the point of setting
				// one. The bot's icon then still beats a donation-value threshold's media,
				// which isn't specific to this gift at all. giftIcon is a bare URL, but
				// everything downstream reads an image object off this list and skips
				// anything without a .url — a raw string silently dropped it from the alert.
				let images = giftMedia.filter(m => m.mime?.[0] == 'image');
				if(!images?.length) images = data.giftTypes?.[msg.serviceId]?.[msg.giftType]?.image && [ data.giftTypes[msg.serviceId][msg.giftType].image ];
				if(!images?.length && msg.giftIcon) images = [ { url: msg.giftIcon } ];
				if(!images?.length && msg.icon) images = [ msg.icon ];
				if(!images?.length) images = thresholdFiles.filter(f => f.mime[0] == 'image');
				if(!images?.length) images = (data.eventTypes?.gift?.media || []).filter(m => m.mime?.[0] == 'image');
				if(!images?.length) images = (data.eventTypes?.alert?.media || []).filter(m => m.mime?.[0] == 'image');
				ret.images = images?.length > 0 && Array.from({ length: msg.multiplier||1 }, () => this.randomItem(images));
			} else if(msg.type == 'tower') {
				let images = msg.media?.filter(f => f?.mime?.[0] == 'image');
				if(!images?.length) images = (data.eventTypes?.tower?.media || []).filter(m => m.mime?.[0] == 'image');
				if(!images?.length) images = (data.eventTypes?.alert?.media || []).filter(m => m.mime?.[0] == 'image');
				ret.images = [ this.randomItem(images) ];
			} else {
				if(msg.icon) ret.images = [ msg.icon ];
				else {
					const eventMedia = data.eventTypes?.[msg.type]?.media || [];
					const alertMedia = isAlert ? (data.eventTypes?.alert?.media || []) : [];
					const allMedia = eventMedia.length > 0 ? eventMedia : alertMedia;
					const images = allMedia.filter(m => m.mime?.[0] == 'image');
					ret.images = images.length ? [ this.randomItem(images) ] : [];
				}
			}
		}

		if(fields.sounds) {
			const alertSounds = [];
			const sounds = [];

			const maybePushRandomItem = (arr, items, comment) => items?.length > 0 && arr.push({ type: 'url', comment, url: this.randomItem(items).url });

			if(isGift) {
				maybePushRandomItem(alertSounds, (data.giftTypes?.[msg.serviceId]?.[msg.giftType]?.media || []).filter(m => m.mime?.[0] == 'audio'), 'gift type');
				const threshold = data.giftMediaThresholds?.toReversed?.()?.find?.(t => t.amount - .001 <= thresholdValue && t.files?.filter?.(f => f.mime[0] == 'audio')?.length > 0);
				maybePushRandomItem(alertSounds, threshold?.files?.filter(f => f.mime[0] == 'audio'), 'gift threshold');
				maybePushRandomItem(alertSounds, (data.eventTypes?.gift?.media || []).filter(m => m.mime?.[0] == 'audio'), 'gift');
			} else if(msg.type == 'tower') {
				maybePushRandomItem(alertSounds, msg.media?.filter(f => f?.mime?.[0] == 'audio'), 'tower media');
				maybePushRandomItem(alertSounds, (data.eventTypes?.tower?.media || []).filter(m => m.mime?.[0] == 'audio'), 'tower');
			} else if(msg.type != 'chat') {
				if(isAlert) {
					maybePushRandomItem(alertSounds, (data.eventTypes?.[msg.type]?.media || []).filter(m => m.mime?.[0] == 'audio'), `alert ${msg.type}`);
				} else {
					maybePushRandomItem(sounds, (data.eventTypes?.[msg.type]?.media || []).filter(m => m.mime?.[0] == 'audio'), `event ${msg.type}`);
				}
			} else if(msg.mml) {
				maybePushRandomItem(sounds, [{ type: 'mml', mml: msg.mml, instrument: msg.sender?.settings?.instrument }], 'mml');
			} else {
				maybePushRandomItem(sounds, (data.eventTypes?.chat?.media || []).filter(m => m.mime?.[0] == 'audio'), 'chat');
			}

			if(isAlert)
				maybePushRandomItem(sounds, (data.eventTypes?.alert?.media || []).filter(m => m.mime?.[0] == 'audio'), 'alert');
			maybePushRandomItem(sounds, (data.eventTypes?.default?.media || []).filter(m => m.mime?.[0] == 'audio'), 'default');

			ret.sounds = [];
			const items = [];
			if(alertSounds?.length > 0) items.push(alertSounds);
			if(ret.speakProlog) {
				items.push({
					type: 'tts',
					isProlog: true,
					speak: ret.speakProlog,
					provider: msg.ttsVoice&&msg.ttsProvider||msg.sender?.settings?.voice_id&&msg.sender?.settings?.voice_provider,
					voice: msg.ttsProvider&&msg.ttsVoice||msg.sender?.settings?.voice_provider&&msg.sender?.settings?.voice_id,
					rate: msg.ttsRate||msg.sender?.settings?.rate,
					pitch: msg.ttsPitch||msg.sender?.settings?.pitch,
					style: msg.ttsStyle||msg.sender?.settings?.style,
					role: msg.ttsRole||msg.sender?.settings?.role,
				});
			}
			if(ret.speak) {
				if(msg.ttsUrl || msg.externalTtsUrl) {
					items.push({ type: 'tts', url: msg.ttsUrl || msg.externalTtsUrl, proxy: msg.externalTtsUrl });
				} else {
					items.push({
						type: 'tts',
						speak: ret.speak,
						provider: msg.ttsVoice&&msg.ttsProvider||msg.sender?.settings?.voice_id&&msg.sender?.settings?.voice_provider,
						voice: msg.ttsProvider&&msg.ttsVoice||msg.sender?.settings?.voice_provider&&msg.sender?.settings?.voice_id,
						rate: msg.ttsRate||msg.sender?.settings?.rate,
						pitch: msg.ttsPitch||msg.sender?.settings?.pitch,
						style: msg.ttsStyle||msg.sender?.settings?.style,
						role: msg.ttsRole||msg.sender?.settings?.role,
					});
				}
			}

			if(msg.song)
				items.push({ type: 'song', song: msg.song });
			if(items.length > 0)
				ret.sounds.push(items.length == 1 ? items[0] : { type: 'any', items });
			ret.sounds.push(sounds);
		}

		if(fields.consoleLog) {
			let con = [ '' ];
			if(msg.serviceName) {
				con[0] += `%c${msg.serviceName}%c `;
				con.push('color: #888');
				con.push('color: inherit');
			}
			if(msg.sender?.avatarUrl) {
				con[0] += '%c   ';
				con.push(`background-image: url('${msg.sender?.avatarUrl}'); background-size: auto 1.2em; background-repeat: no-repeat`);
			}
			if(msg.sender?.displayName) {
				con[0] += `%c${msg.sender?.displayName||msg.sender?.username||'-'}%c`;
				con.push('font-weight: bold');
				con.push('font-weight: normal');
			}
			if(ret.giftString) con.push(ret.giftString);
			if(ret.eventString) con.push(ret.eventString);
			if(ret.spansHtml) {
				con.push(': ');
				con.push(ret.spansHtml);
			}
			ret.consoleLog = con;
		}

		return ret;
	},

	getGiftThreshold(value, thresholds) {
		return thresholds.toReversed().find(t => t.value - .011 <= value) || thresholds?.[0];
	},

	// Returns the new element to append, or null if `msg` was a combo of a gift_types
	// group=true type and got folded into the matching message `container` already
	// has (same service + chatter + gift type — see prepareMessage's groupId) instead
	// of spawning its own. Same grouping the chat/gift widgets do, ported here since
	// the log is plain DOM (no live socket queue to search) — "already has" means
	// whatever's still in `container`, however far its own history/truncation reaches.
	// `container` is optional; omit it (or pass none) to always get a new element,
	// e.g. for a caller with nowhere to search for a match yet.
	createChatLogMessage(msg, data, options, container) {
		const prepared = this.prepareMessage(msg, {
			classList: true,
			serviceIcon: true,
			serviceName: true,
			avatarIcon: true,
			displayNameElem: true,
			spansDom: true,
			linkifySpans: true,
			giftElem: true,
			images: true,
			giftColorThreshold: true,
			mediaElem: true,
			eventString: true,
			canReply: true,
			isAlert: true,
			group: true,
			groupId: true,
		}, data);

		if(prepared.group && container) {
			const existing = Array.from(container.children).findLast(el => el.dataset.groupId === prepared.groupId);
			if(existing) {
				this._mergeChatLogGift(existing, msg, prepared, data);
				return null;
			}
		}

		const div = document.createElement('div');
		div.dataset.id = prepared.dbId;
		if(prepared.id) div.id = prepared.id;
		if(prepared.groupId) div.dataset.groupId = prepared.groupId;
		// The running combo total — bumped by _mergeChatLogGift on a repeat gift, and
		// synced onto prepared.message right before either handler below fires, so a
		// click sees whatever's accumulated by then rather than just this row's first
		// arrival, without every merge needing to reach into `prepared` itself.
		div.dataset.multiplier = String(msg.multiplier || 1);
		div.classList.add('message');
		for(let c of prepared.classList)
			div.classList.add(c);
		if(msg.deleted) div.classList.add('deleted');

		div.onclick = () => {
			if(window.getSelection().focusOffset != window.getSelection().anchorOffset) return;
			// A deleted message shows a placeholder instead of what it said; the first click
			// spends itself revealing that, and only later ones open the message.
			if(div.classList.contains('deleted') && !div.classList.contains('deleted-revealed')) {
				div.classList.add('deleted-revealed');
				return;
			}
			prepared.message.multiplier = parseInt(div.dataset.multiplier, 10) || 1;
			options?.onclick?.(prepared);
		};

		const menuBtn = document.createElement('div');
		menuBtn.classList.add('menubtn');
		menuBtn.onclick = ev => {
			ev.stopPropagation();
			ev.preventDefault();
			prepared.message.multiplier = parseInt(div.dataset.multiplier, 10) || 1;
			options?.onmenuclick?.(prepared);
		};
		div.appendChild(menuBtn);

		if(prepared.serviceIcon) div.appendChild(prepared.serviceIcon);
		if(prepared.avatarIcon) div.appendChild(prepared.avatarIcon);
		if(prepared.displayNameElem) div.appendChild(prepared.displayNameElem);
		if(prepared.giftElem) div.appendChild(prepared.giftElem);
		if(prepared.eventString) div.appendChild(document.createTextNode(prepared.eventString));
		if((prepared.eventString || prepared.giftElem) && prepared.spansDom) div.appendChild(document.createTextNode(' '));
		if(prepared.spansDom) {
			const content = document.createElement('span');
			content.className = 'content';
			prepared.spansDom.forEach(elem => content.appendChild(elem));
			div.appendChild(content);
		}
		if(prepared.mediaElem) div.appendChild(prepared.mediaElem);

		if(msg.type === 'dashboardMessage') {
			div.classList.add('dashboard-message');
			div.appendChild(this.createDashboardIndicators(msg.targets));
		}

		const clearfix = document.createElement('div');
		clearfix.className = 'clearfix';
		div.appendChild(clearfix);

		if(prepared.images?.length) this.placeChatImages(div, prepared.images.filter(i => i?.url).map(i => i.url));

		return div;
	},

	// A grouped gift landing on a message the log already has: bump its running total —
	// see dataset.multiplier above, which onclick/onmenuclick sync onto prepared.message
	// right before firing, so a later "highlight this" click sees the current combo
	// total rather than just this row's first arrival — reword the amount for the new
	// total (not just this arrival's own — generateGiftString multiplies value×multiplier,
	// so the total has to go in), and fold this arrival's own images into the stack — see
	// placeChatImages for the cap/badge/single-vs-multi switch.
	_mergeChatLogGift(existing, msg, prepared, data) {
		const total = (parseInt(existing.dataset.multiplier || '1', 10) || 1) + (msg.multiplier || 1);
		existing.dataset.multiplier = String(total);

		const giftTextElem = existing.querySelector('.gift');
		if(giftTextElem) {
			const { displayString } = this.generateGiftString({ ...msg, multiplier: total }, data?.giftTypes?.[msg.serviceId]?.[msg.giftType]);
			giftTextElem.textContent = displayString;
		}

		if(prepared.images?.length) this.placeChatImages(existing, prepared.images.filter(i => i?.url).map(i => i.url));
	},

	// A message's images, capped at CHAT_IMAGE_STACK_LIMIT with a "+N" badge past that.
	// Always the same .chatImages class regardless of count — each caller's own CSS uses
	// :has(.chatImage:nth-child(N)) to float a lone image beside the text but drop two or
	// more to their own (shrinking) row, so nothing here needs to pick a layout itself.
	// The full url list rides along in a data-images attribute so a later combo merge
	// (see placeChatImages) can pick up the running total.
	_renderChatImages(urls) {
		const imagesDiv = document.createElement('div');
		imagesDiv.className = 'chatImages';
		imagesDiv.dataset.images = JSON.stringify(urls);
		const shown = urls.slice(0, CHAT_IMAGE_STACK_LIMIT);
		for(const url of shown) {
			const imgElem = document.createElement('img');
			imgElem.src = url;
			imgElem.alt = '';
			imgElem.className = 'chatImage';
			imagesDiv.appendChild(imgElem);
		}
		const overflow = urls.length - shown.length;
		if(overflow > 0) {
			const badge = document.createElement('span');
			badge.className = 'chatImages-overflow';
			badge.textContent = `+${overflow}`;
			imagesDiv.appendChild(badge);
		}
		return imagesDiv;
	},

	// (Re)builds a message's images block as the container's first child — always the
	// same spot, since the layout switch (beside the text vs. its own row) is CSS's job,
	// not this function's. `extraUrls` folds onto whatever a previous call already put
	// there (a repeat combo landing on a message that's already showing one) — the
	// running total lives in the block's own data-images attribute, so nothing else
	// needs to track it.
	placeChatImages(container, extraUrls) {
		const old = container.querySelector('.chatImages');
		const urls = [ ...(old ? JSON.parse(old.dataset.images || '[]') : []), ...(extraUrls || []) ].filter(Boolean);
		if(!urls.length) return null;
		const imagesDiv = this._renderChatImages(urls);
		if(old) old.replaceWith(imagesDiv);
		else container.insertBefore(imagesDiv, container.firstChild);
		return imagesDiv;
	},

	// Plain-DOM per-platform send-status indicators for a dashboard message. Kept out of Vue
	// so the chat log stays a pure-DOM fast path; updated in place by updateDashboardIndicator.
	createDashboardIndicators(targets) {
		const container = document.createElement('div');
		container.className = 'dashboard-message-targets';
		(targets || []).forEach((t, i) => {
			const span = document.createElement('span');
			span.className = 'dashboard-message-target';
			span.dataset.index = i;

			if(t.serviceIconUrl) {
				const img = document.createElement('img');
				img.className = 'dmt-service-icon';
				img.src = t.serviceIconUrl;
				img.alt = t.serviceName || '';
				if(t.channelTitle) img.title = t.channelTitle;
				span.appendChild(img);
			}

			const status = document.createElement('span');
			status.className = 'dmt-status';
			span.appendChild(status);

			this._setDashboardTargetStatus(span, t.status || 'sending', t.error);
			container.appendChild(span);
		});
		return container;
	},

	updateDashboardIndicator(messageElem, index, status, error) {
		if(!messageElem) return;
		const span = messageElem.querySelector(`.dashboard-message-target[data-index="${index}"]`);
		if(span) this._setDashboardTargetStatus(span, status, error);
	},

	_setDashboardTargetStatus(span, status, error) {
		span.dataset.status = status;
		const statusEl = span.querySelector('.dmt-status');
		if(statusEl) statusEl.innerHTML = this._dashboardStatusSvg(status);
		const existing = span.querySelector('.dmt-error-popover');
		if(existing) existing.remove();
		if(status === 'error') {
			span.classList.add('dmt-has-error');
			const pop = document.createElement('span');
			pop.className = 'dmt-error-popover';
			pop.textContent = error || 'Failed to send';
			span.appendChild(pop);
		} else {
			span.classList.remove('dmt-has-error');
		}
	},

	_dashboardStatusSvg(status) {
		const attrs = 'viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" xmlns="http://www.w3.org/2000/svg"';
		if(status === 'success')
			return `<svg ${attrs} class="dmt-icon dmt-icon-success"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/></svg>`;
		if(status === 'error')
			return `<svg ${attrs} class="dmt-icon dmt-icon-error"><path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.78.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/></svg>`;
		// sending (spinner)
		return `<svg ${attrs} class="dmt-icon dmt-icon-sending"><path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/><path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/></svg>`;
	},

	timeAgo(then) {
		const now = new Date();
		const diffInSeconds = Math.floor((now - then) / 1000);

		const rtf = new Intl.RelativeTimeFormat(navigator.language || 'en', { numeric: 'auto' });

		const intervals = {
			year: 31536000,
			month: 2592000,
			week: 604800,
			day: 86400,
			hour: 3600,
			minute: 60,
			second: 1,
		};

		for(let unit in intervals) {
			const interval = Math.floor(diffInSeconds / intervals[unit]);
			if(interval >= 1) {
				return rtf.format(-interval, unit);
			}
		}

		return 'just now';
	},

	// time duration for ban times
	// 300 -> "X was banned for 5 minutes"
	timeDuration(seconds) {
		const locale = navigator.language || 'en';

		const divisions = [
			{ unit: 'year', seconds: 31536000 },
			{ unit: 'month', seconds: 2592000 },
			{ unit: 'week', seconds: 604800 },
			{ unit: 'day', seconds: 86400 },
			{ unit: 'hour', seconds: 3600 },
			{ unit: 'minute', seconds: 60 },
			{ unit: 'second', seconds: 1 },
		];

		for (const { unit, seconds: unitSeconds } of divisions) {
			if (Math.abs(seconds) >= unitSeconds || unit === 'second') {
				const value = Math.floor(seconds / unitSeconds);

				const nf = new Intl.NumberFormat(locale, {
					style: 'unit',
					unit,
					unitDisplay: 'long'
				});

				return nf.format(value);
			}
		}
	},
	monthDuration(months) {
		return new Intl.NumberFormat(navigator.language || 'en', {
			style: 'unit',
			unit: 'month',
			unitDisplay: 'long' // 'short' or 'narrow' also available
		}).format(months);
	},

	extractEmojis(text) {
		if(typeof Intl !== "undefined" && Intl.Segmenter) {
			const segmenter = new Intl.Segmenter("en", { granularity: "grapheme" });
			return [...segmenter.segment(text)]
				.map(seg => seg.segment)
				.filter(s =>
					/\p{Extended_Pictographic}/u.test(s) ||           // normal emojis
					/^[\u{1F1E6}-\u{1F1FF}]{2}$/u.test(s)             // country flags (2 regional indicators)
				);
		} else {
			// Fallback: regex that catches both pictographs & flag sequences
			return text.match(
				/\p{Extended_Pictographic}(?:\u200D\p{Extended_Pictographic})*|[\u{1F1E6}-\u{1F1FF}]{2}/gu
			) || [];
		}
	},

	getContrastingTextColor(bgColor, darkColor = '#241e32', lightColor = '#eee9f0') {
		const getLuminance = (hex) => {
			const rgb = hex.replace('#', '').match(/.{2}/g).map(c => {
				const val = parseInt(c, 16) / 255;
				return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
			});
			return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
		};

		const getContrastRatio = (lum1, lum2) => {
			const L1 = Math.max(lum1, lum2);
			const L2 = Math.min(lum1, lum2);
			return (L1 + 0.05) / (L2 + 0.05);
		};

		const bgLum = getLuminance(bgColor);
		const darkLum = getLuminance(darkColor);
		const lightLum = getLuminance(lightColor);

		const contrastWithDark = getContrastRatio(bgLum, darkLum);
		const contrastWithLight = getContrastRatio(bgLum, lightLum);

		return contrastWithDark > contrastWithLight ? darkColor : lightColor;
	},

	formatAmountLabel(amount, noun, options = {}) {
		const {
			locale = navigator.language || 'en',
			pluralSuffix = 's',      // Optional override for irregular plurals
			article = true           // Whether to prepend "a"/"an" for singular
		} = options;

		const isSingular = amount === 1;

		if(isSingular) {
			if(article) {
				const startsWithVowel = /^[aeiou]/i.test(noun);
				const articleText = startsWithVowel ? 'an' : 'a';
				return `${articleText} ${noun}`;
			} else {
				return `1 ${noun}`;
			}
		} else {
			const pluralRules = new Intl.PluralRules(locale);
			const category = pluralRules.select(amount);

			// For now we only handle regular "s" suffix, but allow override
			const pluralNoun =
				category === 'one'
					? noun
					: pluralSuffix === 's'
						? noun + 's'
						: pluralSuffix;

			return `${amount} ${pluralNoun}`;
		}
	},

	// Preload an image and resolve with the HTMLImageElement.
	// urlOrImg: a URL string, or a pre-created Image element (src will be set by this function).
	// options.signal: AbortSignal — rejects with AbortError if fired
	// options.crossOrigin: value for img.crossOrigin (e.g. 'anonymous')
	preloadImage(urlOrImg, { signal, crossOrigin } = {}) {
		return new Promise((resolve, reject) => {
			if(signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));

			const img = typeof urlOrImg === 'string' ? new Image() : urlOrImg;

			if(crossOrigin) img.crossOrigin = crossOrigin;
		img.referrerPolicy = 'no-referrer';

			const cleanup = () => {
				img.removeEventListener('load', onLoad);
				img.removeEventListener('error', onError);
				signal?.removeEventListener('abort', onAbort);
			};

			const onLoad  = () => { cleanup(); resolve(img); };
			const onError = () => { cleanup(); reject(new Error('Image failed to load')); };
			const onAbort = () => {
				cleanup();
				if(img.src) img.src = '';
				reject(new DOMException('Aborted', 'AbortError'));
			};

			img.addEventListener('load', onLoad);
			img.addEventListener('error', onError);
			signal?.addEventListener('abort', onAbort);

			if(img.complete && img.naturalWidth !== 0) {
				cleanup();
				return resolve(img);
			}

			if(typeof urlOrImg === 'string') img.src = urlOrImg;
		});
	},

	// Polyfill for AbortSignal.any() — not available in older Chromium/OBS versions.
	signalAny(signals) {
		if(AbortSignal.any) return AbortSignal.any(signals);
		const ac = new AbortController();
		const abort = () => ac.abort();
		for(const signal of signals) {
			if(signal.aborted) { ac.abort(); return ac.signal; }
			signal.addEventListener('abort', abort, { once: true });
		}
		return ac.signal;
	},
};
